/**
 * "Working in: <client>" bar for Beatly staff. Rendered above every page when
 * the Cowork API reports the session as staff; hidden for clients. Changing the
 * client rewrites the stored scope and reloads so every query re-runs against
 * the selected workspace (docs/BO-ALL-BRANDS-SPEC.md §3.1 in beatly-cowork-api).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Search, Loader2, Eye, Check, Lock, Tag } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { selectClientTeam } from '@/api/beatlyApi';
import { getTokenFromStorage, updateAuthInStorage } from '@/utils/auth';
import { toast } from 'sonner';
import { adoptStaffScopeFromUrl, getStaffScopeTeam, HOUSE, hostOwnsScope, isClientTeamId, listenForHostScope, setStaffScopeTeam } from '@/cowork/staffScope';
import { CLIENT_FEATURE_LABELS } from '@/cowork/clientFeatures';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';

/** A brand named while picking the team ("find a client or brand", or a brand chip): chosen once the new team's brands load. */
const PENDING_BRAND = 'cowork.pendingBrand';
function rememberBrand(teamId: string, name: string | null): void {
  try { if (name) sessionStorage.setItem(PENDING_BRAND, JSON.stringify({ teamId, name })); else sessionStorage.removeItem(PENDING_BRAND); } catch { /* storage unavailable */ }
}
function takePendingBrand(teamId: string | null): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_BRAND);
    if (!raw) return null;
    const p = JSON.parse(raw) as { teamId?: string; name?: string };
    if (p.teamId !== teamId) return null;
    sessionStorage.removeItem(PENDING_BRAND);
    return p.name || null;
  } catch { return null; }
}

/**
 * The brand half of "team + brand": decks, Brand Id, dashboards and the agents all work on ONE brand of the team.
 * In the BO frame Omni's own brand badge (top bar) is hidden, so the pick lives here. A page about one campaign
 * dictates the brand (locked).
 */
function BrandPick({ teamId, tone }: { teamId: string | null; tone: 'client' | 'plain' }) {
  const { brandId, brandName, isDerived, brands, selectBrand, loading } = useEffectiveBrand();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);
  // A brand named while picking the team wins once this team's brands are in.
  useEffect(() => {
    if (!brands.length) return;
    const name = takePendingBrand(teamId);
    const hit = name ? brands.find((b) => b.brandName.toLowerCase() === name.toLowerCase()) : undefined;
    if (hit && hit.brandId !== brandId) selectBrand(hit.brandId);
  }, [brands, teamId, brandId, selectBrand]);

  const cls = tone === 'client' ? 'border-[#f0c47a] bg-white text-[#7a4b00]' : 'border-[#d0d5dd] bg-white text-[#101828]';
  if (!brands.length) return loading ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin opacity-60" /> : <span className="shrink-0 text-[11.5px] opacity-70">no brands in this team yet</span>;
  return (
    <div ref={box} className="relative shrink-0">
      <button type="button" disabled={isDerived} onClick={() => setOpen((o) => !o)} title={isDerived ? 'Set by the campaign you have open' : 'The brand decks, Brand Id, dashboards and the agents work on'} className={`flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-semibold disabled:cursor-default ${cls}`}>
        <Tag className="h-3 w-3 opacity-70" />
        <span className="max-w-[200px] truncate">{brandName || 'Pick a brand'}</span>
        {isDerived ? <Lock className="h-3 w-3 opacity-60" /> : <ChevronDown className="h-3.5 w-3.5 opacity-70" />}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 max-h-[320px] w-[280px] overflow-y-auto rounded-xl border border-[#eaecf0] bg-white p-1.5 text-[#101828] shadow-lg">
          {brands.map((b) => (
            <button key={b.brandId} type="button" onClick={() => { selectBrand(b.brandId); setOpen(false); }} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[#f2f4f7] ${b.brandId === brandId ? 'bg-[#f9f5ff] font-semibold' : ''}`}>
              {b.brandLogoUrl ? <img src={b.brandLogoUrl} alt="" className="h-4 w-4 rounded object-contain" /> : <span className="grid h-4 w-4 place-items-center rounded bg-[#f4ebff] text-[9px] font-bold text-[#7a1fbf]">{b.brandName.slice(0, 1).toUpperCase()}</span>}
              <span className="min-w-0 flex-1 truncate">{b.brandName}</span>
              {b.brandId === brandId && <Check className="h-3.5 w-3.5 text-[#be43ff]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

adoptStaffScopeFromUrl(); // module load = before any page query runs
if (typeof window !== 'undefined') listenForHostScope(); // the BO's Commercial suite switches client/brand live

export function StaffScopeBar() {
  // Inside the back office's Commercial suite the BO's top bar owns the client and what the client sees in their own
  // app ("Client app" beside its picker): nothing of this bar is shown in the frame, and none of its queries run.
  if (hostOwnsScope()) return null;
  return <WorkingInBar />;
}

function WorkingInBar() {
  const me = useQuery({ queryKey: ['cowork', 'me'], queryFn: () => coworkApi.staffScope.me(), staleTime: 5 * 60_000, retry: false });
  const staff = !!me.data?.staff;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // The back office knows thousands of teams and the backend returns at most 200 per call: the search runs
  // server-side on what was typed, never on the first page of an unfiltered list.
  const [dq, setDq] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const teams = useQuery({ queryKey: ['cowork', 'staff-scope', 'teams', dq], queryFn: () => coworkApi.staffScope.teams(dq), enabled: staff, staleTime: 10 * 60_000 });
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const selected = getStaffScopeTeam();
  // The selected team is rarely in the current search page (the list is server-filtered on what was typed), so its name is fetched on its own.
  const selectedTeam = useQuery({ queryKey: ['cowork', 'staff-scope', 'team', selected], queryFn: () => coworkApi.staffScope.team(selected!), enabled: staff && isClientTeamId(selected), staleTime: 10 * 60_000, retry: false });
  // The staff member's own workspace (a beatly.com login is pinned to a real team; a BO token to the house).
  const ownTeamId = me.data?.scope?.actorTeamId || me.data?.teamId || '';
  const ownIsHouse = !ownTeamId || ownTeamId === (teams.data?.house.id || 'beatly-admins');
  const ownName = (me.data?.user as { team?: { name?: string } } | undefined)?.team?.name || teams.data?.teams.find((t) => t.id === ownTeamId)?.name || 'Your team';
  const current = useMemo(() => {
    if (!teams.data) return null;
    if (selected === HOUSE || (!selected && ownIsHouse)) return teams.data.house;
    if (!selected) return { id: ownTeamId, name: ownName, brands: [] as string[] };
    return teams.data.teams.find((t) => t.id === selected) || (selectedTeam.data ? { id: selected, name: selectedTeam.data.team.name, brands: selectedTeam.data.team.brands.map((b) => b.name) } : { id: selected, name: selected, brands: [] as string[] });
  }, [teams.data, selected, ownIsHouse, ownTeamId, ownName, selectedTeam.data]);
  const hits = useMemo(() => (teams.data?.teams || []).slice(0, 80), [teams.data]);
  // Searching a brand name and picking its team picks that brand too.
  // Only when the search named a brand, not the team itself.
  const brandFor = (teamName: string, names: string[]): string | null => {
    const n = q.trim().toLowerCase();
    if (!n || teamName.toLowerCase().includes(n)) return null;
    return names.find((b) => b.toLowerCase().includes(n)) || null;
  };

  const clientTeam = isClientTeamId(selected) && selected !== ownTeamId ? selected : null;
  const qc = useQueryClient();
  // What the client team sees in their own app — flipped here, read by the client app through GET /v1/me.
  const features = useQuery({ queryKey: ['cowork', 'client-features', clientTeam], queryFn: () => coworkApi.staffScope.features(clientTeam!), enabled: staff && !!clientTeam, staleTime: 60_000 });
  const setFeatures = useMutation({
    mutationFn: (list: string[]) => coworkApi.staffScope.setFeatures(clientTeam!, list),
    onSuccess: (data) => qc.setQueryData(['cowork', 'client-features', clientTeam], data),
  });

  if (!staff) return null;

  /**
   * Picking a client respects the chain of JWT authentication: for a Beatly platform login the platform itself
   * mints a token for that team (POST /app/v2/team/:id/select — the same switch the header search does), so every
   * API downstream, the datalake included, sees an ordinary client token. The cowork header only re-points the
   * workspace; it never widens what the token may read. A back-office admin token cannot be exchanged, so for it
   * the header is all there is (data pulls stay empty until the datalake pins admin tokens to a team).
   */
  const choose = async (id: string | null, brandName: string | null = null) => {
    setOpen(false);
    rememberBrand(id || '', brandName);
    const target = id === HOUSE ? null : (id || (isClientTeamId(ownTeamId) ? ownTeamId : null));
    const token = getTokenFromStorage();
    const platformLogin = !!token && !/^admin-/.test(String(me.data?.user?.user_id || ''));
    if (target && platformLogin && isClientTeamId(target)) {
      try {
        const r = await selectClientTeam(target, token!);
        updateAuthInStorage({ token: r.token, user: r.user });
      } catch (e) {
        toast.error(`Could not switch the platform session to that client: ${(e as Error).message}. Data pulls stay on the previous team.`);
      }
    }
    setStaffScopeTeam(id); // null = back to the session's own workspace (no header sent)
    window.location.reload(); // every page re-reads its workspace: simplest correct refresh
  };
  const inClient = !!selected && selected !== HOUSE && selected !== ownTeamId;

  return (
    <div className={`relative z-20 flex items-center gap-3 border-b px-4 py-1.5 text-[12.5px] ${inClient ? 'border-[#f5d9a8] bg-[#fff7e8] text-[#7a4b00]' : 'border-[#eaecf0] bg-white text-[#475467]'}`}>
      <Building2 className="h-3.5 w-3.5 shrink-0 opacity-80" />
      <span className="shrink-0">Working in</span>
      <div ref={box} className="relative">
        <button type="button" onClick={() => setOpen((o) => !o)} className={`flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-semibold ${inClient ? 'border-[#f0c47a] bg-white text-[#7a4b00]' : 'border-[#d0d5dd] bg-white text-[#101828]'}`}>
          {teams.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          <span className="max-w-[260px] truncate">{current?.name || (teams.isError ? 'Back office unavailable' : 'Beatly (house)')}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-1 w-[360px] rounded-xl border border-[#eaecf0] bg-white p-2 text-[#101828] shadow-lg">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#98a2b3]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client or brand" className="w-full rounded-md border border-[#d0d5dd] py-1.5 pl-8 pr-2 text-[12.5px] focus:border-[#be43ff] focus:outline-none" />
            </div>
            <div className="mt-1.5 max-h-[320px] overflow-y-auto">
              {!ownIsHouse && (
                <button type="button" onClick={() => choose(null)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[#f2f4f7] ${!selected || selected === ownTeamId ? 'bg-[#f9f5ff] font-semibold' : ''}`}>
                  <span className="truncate">{ownName}</span><span className="ml-auto text-[11px] text-[#98a2b3]">your own team</span>
                </button>
              )}
              <button type="button" onClick={() => choose(HOUSE)} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[#f2f4f7] ${selected === HOUSE || (!selected && ownIsHouse) ? 'bg-[#f9f5ff] font-semibold' : ''}`}>
                <span className="truncate">Beatly (house)</span><span className="ml-auto text-[11px] text-[#98a2b3]">prospects, internal</span>
              </button>
              {teams.isError && <div className="px-2 py-2 text-[12px] text-[#b42318]">{(teams.error as Error).message}</div>}
              {hits.filter((t) => t.id !== ownTeamId).map((t) => (
                <div key={t.id} className={`rounded-md px-2 py-1.5 hover:bg-[#f2f4f7] ${selected === t.id ? 'bg-[#f9f5ff]' : ''}`}>
                  {/* The team, with the brand the search named (if any); or one of its brands directly. */}
                  <button type="button" onClick={() => choose(t.id, brandFor(t.name, t.brands))} className={`block w-full truncate text-left ${selected === t.id ? 'font-semibold' : ''}`}>{t.name}</button>
                  {t.brands.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {t.brands.slice(0, 8).map((b) => (
                        <button key={b} type="button" onClick={() => choose(t.id, b)} className="rounded-full border border-[#eaecf0] bg-white px-1.5 py-px text-[11px] text-[#475467] hover:border-[#be43ff] hover:text-[#6b21a8]">{b}</button>
                      ))}
                      {t.brands.length > 8 && <span className="text-[11px] text-[#98a2b3]">+{t.brands.length - 8}</span>}
                    </div>
                  )}
                </div>
              ))}
              {teams.data && hits.length === 0 && <div className="px-2 py-2 text-[12px] text-[#98a2b3]">No client matches.</div>}
            </div>
          </div>
        )}
      </div>
      <BrandPick teamId={selected} tone={inClient ? 'client' : 'plain'} />
      {inClient && <span className="hidden truncate text-[11.5px] opacity-80 xl:inline">New decks and dashboards start as "only me" until you share them.</span>}
      {clientTeam && features.data && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5" title="Which agent pages this client sees in their own app">
          <Eye className="h-3.5 w-3.5 opacity-70" />
          <span className="hidden md:inline">Client sees</span>
          {features.data.available.map((f) => {
            const on = features.data!.features.includes(f);
            return (
              <button
                key={f}
                type="button"
                disabled={setFeatures.isPending}
                onClick={() => setFeatures.mutate(on ? features.data!.features.filter((x) => x !== f) : [...features.data!.features, f])}
                className={`rounded-full border px-2 py-0.5 text-[11.5px] font-medium transition ${on ? 'border-[#be43ff] bg-[#f9f0ff] text-[#6b21a8]' : 'border-[#d0d5dd] bg-white text-[#667085] hover:border-[#98a2b3]'}`}
              >
                {CLIENT_FEATURE_LABELS[f] || features.data!.labels?.[f] || f}
              </button>
            );
          })}
          {features.data.features.length === 0 && <span className="text-[11px] text-[#98a2b3]">nothing yet</span>}
        </div>
      )}
    </div>
  );
}
